import Filters, { FilterComponent } from "@/components/Filters"
import { cashbookServices } from "@/modules/finances/cashbooks/services/cashbook.services"
import { CashBookData, CashBookStatus, CreateCashBookDto, MAP_CASHBOOK_STATUS } from "@/modules/finances/cashbooks/types"
import { fetchUsers } from "@/modules/manages/users/services/users.services"
import { formatCurrency } from "@/utils/convert"
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Modal, Space, Table, Tag, Typography, message } from "antd"
import { SorterResult } from "antd/es/table/interface"
import dayjs from "dayjs"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { AnyObject } from "yup/lib/types"
import UpsertForm from "./UpsertForm"

type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>

const List: React.FC = () => {
  const [dataSource, setDataSource] = useState<CashBookData>()
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()
  const location = useLocation()
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [dataUsers, setDataUsers] = useState<{ value: string; label: string }[]>([])
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [updateModalVisible, setUpdateModalVisible] = useState(false)
  const [selectedCashBook, setSelectedCashBook] = useState<any>(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [cashBookToDelete, setCashBookToDelete] = useState<any>(null)

  const getUsers = async () => {
    try {
      const response = await fetchUsers()
      if (response?.data) {
        const data = response.data.items.map((item: any) => ({ value: item.id, label: item.fullname }))
        setDataUsers(data)
      }
    } catch (error) {
      console.error("Error fetching users:", error)
    }
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
  }

  const showDetail = (id: number) => {
    navigate(`/finances/cashbooks/${id}${location.search}`)
  }

  const showPrintView = (id: number) => {
    navigate(`/finances/cashbooks/${id}/print`)
  }

  const handleCreateCashBook = async (data: CreateCashBookDto) => {
    try {
      const response = await cashbookServices.createCashBook(data)
      

      if (response?.statusCode === 400) {
        if (response.message?.includes('Đã có sổ quỹ cho ngày này')) {
          message.error('Đã có sổ quỹ cho ngày này. Vui lòng chọn ngày khác!')
        } else {
          message.error(response.message || 'Có lỗi xảy ra khi tạo sổ quỹ!')
        }
        return
      }
      
      if (response?.data) {
        message.success('Tạo sổ quỹ thành công!')
        setCreateModalVisible(false)
        // Refresh data
        fetchData()
      }
    } catch (error: any) {
    
      if (error?.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('Có lỗi xảy ra khi tạo sổ quỹ!')
      }
    }
  }

  const handleCreateCashBookClick = () => {
    setCreateModalVisible(true)
  }

  const handleUpdateCashBookClick = (record: any) => {
    setSelectedCashBook(record)
    setUpdateModalVisible(true)
  }

  const handleUpdateCashBook = async (data: any) => {
    try {
      const response = await cashbookServices.updateCashBook(selectedCashBook.id, data)
      
      
      // Check if response has error status
      if (response?.statusCode === 400) {
        if (response.message?.includes('Đã có sổ quỹ cho ngày này')) {
          message.error('Đã có sổ quỹ cho ngày này. Vui lòng chọn ngày khác!')
        } else {
          message.error(response.message || 'Có lỗi xảy ra khi cập nhật sổ quỹ!')
        }
        return
      }
      
      if (response?.data) {
        message.success('Cập nhật sổ quỹ thành công!')
        setUpdateModalVisible(false)
        setSelectedCashBook(null)
        // Refresh data
        fetchData()
      }
    } catch (error: any) {
      // Handle network errors or other exceptions
      if (error?.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('Có lỗi xảy ra khi cập nhật sổ quỹ!')
      }
    }
  }

  const handleDeleteCashBookClick = (record: any) => {
    setCashBookToDelete(record)
    setDeleteModalVisible(true)
  }

  const handleDeleteCashBook = async () => {
    if (!cashBookToDelete) return
    
    try {
      await cashbookServices.deleteCashBook(cashBookToDelete.id)
      message.success('Xóa sổ quỹ thành công!')
      setDeleteModalVisible(false)
      setCashBookToDelete(null)
      // Force refresh data bằng cách gọi lại API trực tiếp
      setLoading(true)
      try {
        const searchParams = queryString.parse(location.search)
        const response = await cashbookServices.getCashBooks(searchParams)
        if (response?.data?.data) {
          setDataSource({
            items: response.data.data,
            meta: {
              totalItems: response.data.total,
              itemsPerPage: response.data.limit,
              currentPage: response.data.page,
              itemCount: response.data.data.length,
              totalPages: Math.ceil(response.data.total / response.data.limit)
            }
          })
        }
      } catch (error) {
        console.error("Error refreshing data:", error)
      } finally {
        setLoading(false)
      }
    } catch (error: any) {
      // Handle network errors or other exceptions
      if (error?.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error('Có lỗi xảy ra khi xóa sổ quỹ!')
      }
    }
  }

  const onTableChanged = ((pagination, _, sorter: SorterResult<AnyObject>) => {
    const sortOptions: { [key: string]: string } = sorter.order
      ? { [sorter.field as string]: sorter.order === "descend" ? "desc" : "asc" }
      : {}
    const paginationOptions: { [key: string]: string } = {
      page: `${pagination.current}`,
      limit: `${pagination.pageSize}`,
    }

    if (pagination && pagination.current) {
      setCurrentPage(pagination.current)
    }

    setSortPaginationValues({ ...sortOptions, ...paginationOptions })
    const searchParams = queryString.stringify({ ...filterValues, ...sortOptions, ...paginationOptions })

    navigate(searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname)
  }) as EditableTableProps["onChange"]

  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: "5%",
    },
    {
      title: "Khoảng thời gian",
      dataIndex: "from_date",
      key: "from_date",
      width: "12%",
      render: (from_date: string, record: any) => {
        const fromDate = dayjs(from_date).format("DD/MM/YYYY HH:mm")
        const toDate = dayjs(record.to_date).format("DD/MM/YYYY HH:mm")
        return `${fromDate} - ${toDate}`
      },
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: sortPaginationValues
        ? sortPaginationValues.from_date
          ? sortPaginationValues.from_date === "asc"
            ? "ascend"
            : "descend"
          : null
        : null,
    },
    {
      title: "Tên sổ quỹ",
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: "Số dư đầu kỳ",
      dataIndex: "opening_balance",
      key: "opening_balance",
      width: "15%",
      render: (amount: number) => formatCurrency(amount),
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: sortPaginationValues
        ? sortPaginationValues.opening_balance
          ? sortPaginationValues.opening_balance === "asc"
            ? "ascend"
            : "descend"
          : null
        : null,
    },
    {
      title: "Số dư cuối kỳ",
      dataIndex: "closing_balance",
      key: "closing_balance",
      width: "15%",
      render: (amount: number, record: any) => {
        if (record.status === CashBookStatus.CLOSED) {
          return formatCurrency(amount || 0)
        }
        return "-"
      },
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: sortPaginationValues
        ? sortPaginationValues.closing_balance
          ? sortPaginationValues.closing_balance === "asc"
            ? "ascend"
            : "descend"
          : null
        : null,
    },
    {
      title: "Tổng thu",
      dataIndex: "total_income",
      key: "total_income",
      width: "12%",
      render: (amount: number) => formatCurrency(amount || 0),
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: sortPaginationValues
        ? sortPaginationValues.total_income
          ? sortPaginationValues.total_income === "asc"
            ? "ascend"
            : "descend"
          : null
        : null,
    },
    {
      title: "Tổng chi",
      dataIndex: "total_expense",
      key: "total_expense",
      width: "12%",
      render: (amount: number) => formatCurrency(amount || 0),
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: sortPaginationValues
        ? sortPaginationValues.total_expense
          ? sortPaginationValues.total_expense === "asc"
            ? "ascend"
            : "descend"
          : null
        : null,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: "10%",
      render: (status: CashBookStatus) => (
        <Tag color={status === CashBookStatus.ACTIVE ? "green" : "red"}>
          {MAP_CASHBOOK_STATUS[status]}
        </Tag>
      ),
      filters: [
        { text: "Đang mở", value: CashBookStatus.ACTIVE },
        { text: "Đã chốt sổ", value: CashBookStatus.CLOSED },
      ],
    },
    {
      title: "Thao tác",
      key: "action",
      dataIndex: "action",
      width: "10%",
      render: (_, record: any) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showDetail(record.id)}
          >
            Xem
          </Button>
          {record.status === CashBookStatus.ACTIVE && (
            <>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleUpdateCashBookClick(record)}
            >
              Sửa
            </Button>
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCashBookClick(record)}
            >
              Xóa
            </Button>
            </>
          )}
          {record.status === CashBookStatus.CLOSED && (
            <Button
              type="link"
              icon={<PrinterOutlined />}
              onClick={() => showPrintView(record.id)}
            >
              In sổ quỹ
            </Button>
          )}
        </Space>
      ),
    },
  ]

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const searchParams = queryString.parse(location.search)
      const response = await cashbookServices.getCashBooks(searchParams)
      if (response?.data?.data) {
        // Transform response để khớp với interface
        setDataSource({
          items: response.data.data,
          meta: {
            totalItems: response.data.total,
            itemsPerPage: response.data.limit,
            currentPage: response.data.page,
            itemCount: response.data.data.length,
            totalPages: Math.ceil(response.data.total / response.data.limit)
          }
        })
      }
    } catch (error) {
      console.error("Error fetching cash books:", error)
    } finally {
      setLoading(false)
    }
  }, [location.search])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: any } = {}
    const sortPaginationValues: { [key: string]: string } = {}
    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "from_date" || key === "to_date") {
          filterValues[key] = dayjs(value)
          continue
        }
        if (key === "created_by_id") {
          filterValues[key] = value
          continue
        }
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          if (key === "page") {
            setCurrentPage(parseInt(value))
          }
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setFilterValues(filterValues)
    setSortPaginationValues(sortPaginationValues)
    setDefaultFilterValues({ ...filterValues, ...sortPaginationValues })
  }, [location.search])

  useEffect(() => {
    getUsers()
  }, [])

  const filterFunction = useCallback(() => {
    fetchData()
  }, [fetchData])

  const onFilterChange = (values: object) => {
    let newValue = { ...values, ...sortPaginationValues }

    if (newValue.from_date) {
      newValue = { ...newValue, from_date: dayjs(newValue.from_date).format("YYYY-MM-DD") }
    }

    if (newValue.to_date) {
      newValue = { ...newValue, to_date: dayjs(newValue.to_date).format("YYYY-MM-DD") }
    }

    if (newValue.created_by_id === "") {
      delete newValue.created_by_id
    }

    setFilterValues(newValue as { [key: string]: string })
    setSortPaginationValues(null)
    const searchParams = queryString.stringify(newValue)
    navigate(searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname)
  }
  



  return (
    <div style={{ padding: "24px" }}>
      <Card
        type="inner"
        size="small"
        title={
          <Flex justify="space-between" align="center">
            <Typography.Title level={4} style={{ margin: 0 }}>
              Danh sách sổ quỹ
            </Typography.Title>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleCreateCashBookClick}
              >
                Tạo mới
              </Button>
              <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                Làm mới
              </Button>
            </Space>
          </Flex>
        }
      >
        <Filters
          filterRef={filterRef}
          onSubmit={onFilterChange}
          defaultFilterValues={defaultFilterValues}
          filters={[
            {
              label: "Lọc theo ngày từ",
              placeholder: "Vui lòng chọn ngày từ",
              name: "from_date",
              type: "date",
              defaultValue: null,
            },
            {
              label: "Lọc theo ngày đến",
              placeholder: "Vui lòng chọn",
              name: "to_date",
              type: "date",
              defaultValue: null,
            },
            {
              label: "Lọc theo trạng thái",
              placeholder: "Vui lòng chọn loại",
              name: "status",
              type: "select",
              options: [
                { value: "", label: "Tất cả" },
                { value: CashBookStatus.ACTIVE, label: "Đang mở" },
                { value: CashBookStatus.CLOSED, label: "Đã chốt sổ" },
              ],
              defaultValue: "",
            },
            {
              label: "Lọc theo người tạo",
              placeholder: "Vui lòng chọn người tạo",
              name: "created_by_id",
              type: "select-search",
              options: dataUsers,
              defaultValue: "",
            },
          ] as FilterComponent[]}
        />
        <Table
          columns={defaultColumns}
          dataSource={dataSource?.items || []}
          rowKey="id"
          loading={loading}
          onChange={onTableChanged}
          pagination={{
            current: currentPage,
            pageSize: dataSource?.meta?.itemsPerPage || 20,
            total: dataSource?.meta?.totalItems || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} sổ quỹ`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <UpsertForm
        visible={createModalVisible}
        mode="create"
        onCancel={() => setCreateModalVisible(false)}
        onSubmit={handleCreateCashBook}
        loading={loading}
      />

      <UpsertForm
        visible={updateModalVisible}
        mode="update"
        initialData={selectedCashBook}
        onCancel={() => {
          setUpdateModalVisible(false)
          setSelectedCashBook(null)
        }}
        onSubmit={handleUpdateCashBook}
        loading={loading}
      />

      <Modal
        title="Xác nhận xóa sổ quỹ"
        open={deleteModalVisible}
        onOk={handleDeleteCashBook}
        onCancel={() => {
          setDeleteModalVisible(false)
          setCashBookToDelete(null)
        }}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
      >
        <p>
          Bạn có chắc chắn muốn xóa sổ quỹ "{cashBookToDelete?.name}" 
          ({dayjs(cashBookToDelete?.from_date).format('DD/MM/YYYY HH:mm')} - {dayjs(cashBookToDelete?.to_date).format('DD/MM/YYYY HH:mm')})?
        </p>
        <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          Hành động này không thể hoàn tác!
        </p>
      </Modal>
    </div>
  )
}

export default List
